import { CoreInput } from '@/components/Form/InputAntForm';
import { CoreSwitch } from '@/components/Form/Switch';
import { PERMISSIONS } from '@/contants';
import { useUserPermissions } from '@/core/hooks/useUserPermissions';
import { BusinessUnitEntity } from '@/domains/business-unit/entities';
import { Button, Typography } from 'antd';
import { get } from 'lodash';
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { BusinessDomainPicker } from '../../components/BusinessDomainPicker';
import { BusinessUnitCategoryPicker } from '../../components/BusinessUnitCategoryPicker';
import { BusinessUnitChildrenPicker } from '../../components/BusinessUnitChildrenPicker';
import { OfferingProductGroupPicker } from '../../components/OfferingProductGroupPicker';
import { SalesTargetRankingDisplay } from '../../components/SalesTargetRankingDisplay';
import useViewModel from '../../viewmodels/business-unit-form.viewmodel';

interface Props {
  detailBusinessUnit: BusinessUnitEntity;
  openForm: boolean;
}

export const BusinessUnitForm = (props: Props) => {
  const { openForm, detailBusinessUnit } = props;
  const [currentQueryParameters] = useSearchParams();
  const showId = currentQueryParameters.get('id');
  const { hasPermission } = useUserPermissions();
  const { methodForm, initialValue, onSubmit } = useViewModel(props);
  const {
    control,
    reset,
    formState: { isSubmitting, isDirty },
  } = methodForm;

  const canSubmit = showId
    ? hasPermission(PERMISSIONS.BUSINESS_UNITS.EDIT)
    : hasPermission(PERMISSIONS.BUSINESS_UNITS.CREATE);
  const canDeactivate = hasPermission(PERMISSIONS.BUSINESS_UNITS.DEACTIVATE);

  useEffect(() => {
    if (showId && detailBusinessUnit) {
      reset({
        id: detailBusinessUnit?.id ?? '',
        name: detailBusinessUnit?.name ?? '',
        status: detailBusinessUnit?.status ?? 'ACTIVE',
        parentId: detailBusinessUnit?.parentId ?? '',
        businessDomainId: detailBusinessUnit?.businessDomainId ?? '',
        childrenIds: detailBusinessUnit?.childrenIds ?? [],
        categoryIds: detailBusinessUnit?.categoryIds ?? [],
        offeringProductGroupIds: detailBusinessUnit?.offeringProductGroupIds ?? [],
      });
    } else if (!showId) {
      reset(initialValue);
    }
  }, [openForm, showId, detailBusinessUnit]);

  return (
    <FormProvider {...methodForm}>
      <form onSubmit={onSubmit}>
        <div className='w-full flex'>
          <div className='px-3 mb-6'>
            <Typography.Title level={4}>
              {showId ? get(detailBusinessUnit, 'name') : 'Create'}
            </Typography.Title>
          </div>
          <div className='ml-auto'>
            <CoreSwitch
              control={control}
              name='status'
              className='px-3 mt-2'
              formatValue={true}
              checkedValue='ACTIVE'
              unCheckedValue='INACTIVE'
              disabled={!canDeactivate}
            />
          </div>
        </div>
        <div className='w-full flex flex-wrap'>
          <CoreInput control={control} name='name' label='Name' className='w-full mb-5 px-3' />
          <BusinessUnitChildrenPicker className='w-full px-3 mb-5' />
          <BusinessDomainPicker className='w-full px-3 mb-3' />
          <BusinessUnitCategoryPicker className='w-full px-3 mb-5' />
          <OfferingProductGroupPicker className='w-full px-3 mb-5' />
        </div>

        {showId && <SalesTargetRankingDisplay detailBusinessUnit={detailBusinessUnit} />}

        <div className='text-right px-3 mt-3'>
          <Button
            type='primary'
            htmlType='submit'
            loading={isSubmitting}
            disabled={!isDirty || !canSubmit}
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
